import {
    PageType,
    GetRouteAdTargeting,
    getBaseUrl,
} from '@news-mono/web-common'
import { layout } from '../../../App.routing'
import { metrics } from '@news-mono/component-library'
import {
    ArticleV4DTO,
    PageMetaDTO,
    SectionMetaInfoDTO,
} from '@west-australian-newspapers/publication-types'
import { BaseClientConfig } from '@news-mono/web-common'
import { AdTargeting } from '@news-mono/web-common'
import { TheWestSection } from '@news-mono/component-library'

// Used for GA / Quicklinks background coloring
export const featurePublicationPageType = 'feature-publication'

export const createFeatureRoute = ({
    article,
    config,
    section,
    getAdTargeting,
    sectionMeta,
    pageMeta,
}: {
    article: ArticleV4DTO
    config: BaseClientConfig
    section: TheWestSection
    getAdTargeting: GetRouteAdTargeting
    sectionMeta: SectionMetaInfoDTO
    pageMeta?: PageMetaDTO
}): PageType<TheWestSection> => {
    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        'article',
        section,
        article.topics.primary,
        ...article.topics.secondary,
    )

    const adTargeting: AdTargeting = {
        pageId: article.slug,
        adUnitPath,
        ssAdUnits,
        topics,
    }
    const baseUrl = getBaseUrl(config.publicUrl, sectionMeta)
    const articleUrl = `${baseUrl}/${article.topics.primary.id}/${article.slug}`
    const requiredAccess = article.requiredAccess ?? {
        level: 'anonymous',
    }

    return {
        kind: 'page',
        heading: article.heading,
        pageMeta: {
            ...pageMeta,
            description: article.homepageTeaser,
        },
        socialMeta: {
            title: article.socialHead || article.homepageHead,
            description: article.socialTeaser || article.homepageTeaser,
        },
        hideHeading: true,
        noMetaTitleTemplate: false,
        pageType: featurePublicationPageType,
        adTargeting,
        additionalPageProperties: {
            primaryTopic: article.topics.primary,
            secondaryTopics: article.topics.secondary,
            publicationKind:
                article.layoutView === 'feature'
                    ? 'feature-article'
                    : article.kind,
        },
        section,
        compositions: [
            layout.composition({
                type: 'box',
                props: {
                    containerWidth: '100%',
                    hasBackgroundFill: true,
                    verticalGutters: ['unset', 'md'],
                },
                contentAreas: {
                    main: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                hasTopSpacing: false,
                                containerWidth:
                                    metrics.thewest.featureContentWidth,
                                hasBackgroundFill: true,
                            },
                            contentAreas: {
                                main: [
                                    layout.nestedComposition({
                                        type: 'article-breach-composition',
                                        props: {
                                            requiredAccess,
                                            publicationId: article.id,
                                            slug: article.slug,
                                        },
                                        contentAreas: {
                                            common: [
                                                layout.component({
                                                    type: 'article-hero',
                                                    props: {
                                                        adUnitPath,
                                                        heading:
                                                            article.heading,
                                                        publicationPathname:
                                                            article.slug,
                                                        imageRenderType:
                                                            'feature',
                                                        figcaptionRenderOptions:
                                                            'hidden',
                                                    },
                                                }),
                                                // This nested composition controls the feature-headline & associated content offset
                                                layout.nestedComposition({
                                                    type: 'box',
                                                    props: {
                                                        containerWidth: 1220,
                                                        verticalSpacing: 'lg',
                                                        positionOffset: {
                                                            direction: 'top',
                                                            value: 40,
                                                        },
                                                        breakpoints: {
                                                            xxs: {
                                                                verticalSpacing:
                                                                    'md',
                                                                positionOffset: {
                                                                    direction:
                                                                        'top',
                                                                    value: 100,
                                                                },
                                                            },
                                                            sm: {
                                                                verticalSpacing:
                                                                    'md',
                                                                positionOffset: {
                                                                    direction:
                                                                        'top',
                                                                    value: 90,
                                                                },
                                                            },
                                                            md: {
                                                                verticalSpacing:
                                                                    'xl',
                                                                positionOffset: {
                                                                    direction:
                                                                        'top',
                                                                    value: 110,
                                                                },
                                                            },
                                                        },
                                                    },
                                                    contentAreas: {
                                                        main: [
                                                            layout.component({
                                                                type:
                                                                    'feature-headline',
                                                                props: {
                                                                    text:
                                                                        article.heading,
                                                                    classification:
                                                                        article.classification,
                                                                },
                                                            }),
                                                            layout.component({
                                                                type:
                                                                    'figcaption',
                                                                props: {
                                                                    figcaptionLayout:
                                                                        'feature-article-position',
                                                                    kind:
                                                                        'hero-image',
                                                                    separatedFromMedia: true,
                                                                },
                                                            }),
                                                        ],
                                                    },
                                                }),
                                            ],
                                            main: [
                                                layout.nestedComposition({
                                                    type: 'feature-composition',
                                                    props: {
                                                        containerWidth:
                                                            metrics.thewest
                                                                .siteMetrics
                                                                .mainContentWidth,
                                                        horizontalGutters: true,
                                                        hasBackgroundFill: true,
                                                        stretchSelf: true,
                                                        horizontalGap: 'sm',
                                                    },
                                                    contentAreas: {
                                                        main: [
                                                            layout.component({
                                                                type:
                                                                    'article-feature',
                                                                props: {
                                                                    meta: {
                                                                        slug:
                                                                            article.slug,
                                                                        kind:
                                                                            'article',
                                                                        inlinePublicationContent: [
                                                                            {
                                                                                kind:
                                                                                    'ad',
                                                                                insertAfter: 3,
                                                                                content: [
                                                                                    {
                                                                                        noticePosition:
                                                                                            'none',
                                                                                        hiddenUntilLoaded: true,
                                                                                        padding: [
                                                                                            0,
                                                                                            0,
                                                                                            metrics
                                                                                                .thewest
                                                                                                .margins
                                                                                                .md,
                                                                                            0,
                                                                                        ],
                                                                                        slot: {
                                                                                            id:
                                                                                                'article-mrec-one',
                                                                                            size:
                                                                                                'alwaysMrec',
                                                                                            pageType:
                                                                                                'article',
                                                                                        },
                                                                                    },
                                                                                ],
                                                                            },
                                                                            {
                                                                                kind:
                                                                                    'oovvuu-featured-card',
                                                                                insertAfter: 7,
                                                                                props: {
                                                                                    oovvuuUrl:
                                                                                        config.oovvuuUrl,
                                                                                    oovvuuPath:
                                                                                        config.oovvuuPath,
                                                                                },
                                                                            },
                                                                        ],
                                                                    },
                                                                    dataDefinitionArgs: {
                                                                        publicationId:
                                                                            article.id,
                                                                    },
                                                                    section,
                                                                    isVideoStickyEnabled: true,
                                                                },
                                                            }),

                                                            layout.component({
                                                                type:
                                                                    'content-separator',
                                                                props: {
                                                                    verticalSpacing:
                                                                        'lg',
                                                                },
                                                            }),
                                                        ],
                                                        sidebar: [
                                                            layout.component({
                                                                type: 'ad-unit',
                                                                props: {
                                                                    noticePosition:
                                                                        'none',
                                                                    padding: [
                                                                        metrics
                                                                            .thewest
                                                                            .margins
                                                                            .xxl,
                                                                        0,
                                                                        metrics
                                                                            .thewest
                                                                            .margins
                                                                            .md,
                                                                        0,
                                                                    ],
                                                                    slot: {
                                                                        id:
                                                                            'sidebar-top',
                                                                        size:
                                                                            'desktopMrecHalfPage',
                                                                        pageType:
                                                                            'article',
                                                                    },
                                                                },
                                                            }),
                                                        ],
                                                        skinny: [
                                                            layout.component({
                                                                type: 'sharing',
                                                                props: {
                                                                    text:
                                                                        article.heading,
                                                                    url: articleUrl,
                                                                    shareOptions: [
                                                                        'facebook',
                                                                        'twitter',
                                                                        'email',
                                                                        'clipboard',
                                                                    ],
                                                                    isSticky: true,
                                                                    layout:
                                                                        'stacked-md-desktop',
                                                                },
                                                            }),
                                                        ],
                                                    },
                                                }),
                                            ],
                                            comments: [
                                                layout.nestedComposition({
                                                    type: 'box',
                                                    props: {
                                                        verticalSpacing: 'lg',
                                                        hasTopSpacing: true,
                                                    },
                                                    contentAreas: {
                                                        main: [
                                                            layout.component({
                                                                type: 'coral',
                                                                feature:
                                                                    'comments',
                                                                props: {
                                                                    storyID: `${config.publicHostname}-${article.id}`,
                                                                    horizontalSpacing:
                                                                        'md',
                                                                    articleCommentsEnabled:
                                                                        article.allowCommenting,
                                                                },
                                                            }),
                                                        ],
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ],
    }
}
